import { createTheme } from '@mui/material/styles';

/**
 * The app's MUI theme — which until now did not exist.
 *
 * WHY IT HAD TO: A DROPDOWN INSIDE A DIALOG WAS UNREACHABLE.
 *
 * The shell's sidebar is `z-[6000]` (Sidebar.tsx), well above MUI's default
 * modal layer of 1300, so every dialog in the product would have opened UNDER
 * the navigation. Modules worked around that one dialog at a time, with
 * `sx={{ zIndex: 7000 }}` on the Dialog itself — there are two dozen of those
 * in finance2 alone.
 *
 * That fixed the dialog and broke what opens from inside it. A Select's menu is
 * NOT a child of the dialog: MUI portals it to <body> as its own modal layer,
 * at the theme default of 1300. Against a dialog raised to 7000 it therefore
 * opens BEHIND the paper — the field flips its arrow, the list paints under the
 * dialog, and nothing can be picked. Add Voucher's Voucher Type, UDA, Fund and
 * Department pickers were all dead this way, and so were the year and authority
 * pickers on the budget upload. The same is true of every Tooltip inside a
 * dialog, which is how a disabled button's explanation went missing.
 *
 * There is no per-dialog fix for that, only a per-SELECT one — a `MenuProps`
 * override on every dropdown in the application. So the layering is settled
 * here instead, once:
 *
 *   drawer   6200   above the sidebar, so a Drawer is not cut off by it
 *   modal    7200   dialogs, popovers, menus and select lists
 *   snackbar 7300   a confirmation stays visible over the dialog that caused it
 *   tooltip  7400   the topmost transient thing, as MUI intends
 *
 * A dialog that still carries its own `zIndex: 7000` keeps it and stays ABOVE
 * the sidebar and BELOW its own menus, which is the correct order and the
 * reason 7200 was chosen rather than 6100. Those overrides are now redundant
 * rather than wrong, and are left alone.
 *
 * Everything above 9990 in this codebase — the report-issue overlay, the global
 * ticket modal, the screenshot tool — is deliberately topmost and is left above
 * all of this.
 *
 * Nothing else is themed here. The application had no MUI ThemeProvider at all,
 * so every component has been rendering against MUI's defaults; adding palette
 * or typography to this object would restyle the entire product as a side
 * effect of fixing a stacking bug.
 */
export const muiTheme = createTheme({
  /* The one palette entry: pages that paint a full-page Box in
     "background.default" sit on the shell's page ground (--page-ground in
     index.css: cream, or dark brown in dark mode) rather than a white sheet.
     Nothing in the codebase runs colour maths on this value, so a CSS variable
     is safe here. */
  palette: {
    background: { default: 'var(--page-ground)' },
  },
  zIndex: {
    drawer: 6200,
    modal: 7200,
    snackbar: 7300,
    tooltip: 7400,
  },
});

export default muiTheme;
